// Top-of-page KPI tile. Clickable; opens the drill panel for the metric.

import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { ACCENTS, type AccentToken } from './peopleInsightsTokens';

export interface KpiTileProps {
  label: string;
  value: string;
  hint?: string;
  /** Delta string like "+22%" rendered as a small trend badge. */
  delta?: string;
  trend?: 'up' | 'down';
  icon: React.ReactNode;
  accent?: AccentToken;
  onClick?: () => void;
}

const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  hint,
  delta,
  trend,
  icon,
  accent = 'indigo',
  onClick,
}) => {
  const a = ACCENTS[accent];
  const trendColor = trend === 'up' ? ACCENTS.emerald.on : ACCENTS.rose.on;
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        textAlign: 'left',
        alignItems: 'stretch',
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        p: 2,
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': {
          borderColor: 'rgba(0, 0, 0, 0.18)',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
        },
      }}
    >
      <Stack spacing={1.25} sx={{ width: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              backgroundColor: a.surface,
              color: a.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          {delta && (
            <Typography
              variant="caption"
              sx={{
                color: trendColor,
                fontWeight: 600,
                lineHeight: 1,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: trend === 'up' ? ACCENTS.emerald.surface : ACCENTS.rose.surface,
              }}
            >
              {delta}
            </Typography>
          )}
        </Stack>
        <Box>
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              lineHeight: 1.15,
              color: 'text.primary',
              letterSpacing: '-0.01em',
            }}
          >
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {label}
          </Typography>
          {hint && (
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', display: 'block', mt: 0.25 }}
            >
              {hint}
            </Typography>
          )}
        </Box>
      </Stack>
    </ButtonBase>
  );
};

export default KpiTile;
